import React from 'react';
import type { components } from '../../api/types.gen';
import type { SchoolConfig } from './panels/ScheduleConfigPanel';
import styles from './ScheduleForm.module.css';

/**
 * The A/B-day tag carried by each in-memory form period. Regular-schedule
 * periods are untagged (null/undefined); block/mixed schedules group by
 * 'A'/'B', and 'Both'/'both' marks a shared period shown on both days.
 */
export type ScheduleFormAbDay = 'A' | 'B' | 'Both' | 'both' | null;

/**
 * One editable period row as the parent (ScheduleVariantModal) keeps it in
 * memory. Trimmed to the fields this form edits — `schoolPeriodId` references
 * the canonical SchoolPeriod, `start`/`end` are 24-hour HH:MM strings, and
 * `abDay` is the local grouping tag. `order` is derived by the parent on save.
 */
export interface ScheduleFormPeriod {
  schoolPeriodId: number | null;
  start: string;
  end: string;
  abDay?: ScheduleFormAbDay;
}

/** Minimal canonical SchoolPeriod shape this form reads (id + display name). */
export type ScheduleFormSchoolPeriod = Pick<
  components['schemas']['SchoolPeriodResponse'],
  'id' | 'name'
>;

export interface ScheduleFormProps {
  periods: ScheduleFormPeriod[];
  onPeriodsChange: (periods: ScheduleFormPeriod[]) => void;
  schoolConfig?: SchoolConfig | null;
  schoolPeriods?: ScheduleFormSchoolPeriod[];
  onCreateSchoolPeriod: (
    name: string
  ) => Promise<components['schemas']['SchoolPeriodResponse'] | null>;
  disabled?: boolean;
}

// Per-period fields are trimmed to {schoolPeriodId, start, end} (plus the
// abDay tag used only for local A/B/Both grouping) — hallpass-backend's
// Period model has no passBlockingStart/passBlockingEnd/limitlessOverride/
// includeInUploads equivalent (confirmed dropped as part of the Firebase ->
// hallpass-backend migration, see ScheduleVariantModal.js's header
// comment). `order` isn't user-editable here; it's derived from each
// period's position in its A/B/single list when the parent modal saves.
//
// schoolPeriodId references the school's canonical period identity
// (SchoolPeriod — see docs/SCHEMA_PLAN.md), managed separately in
// SchoolPeriodsPanel.js, rather than a free-typed name — this is what keeps
// "Period 4" meaning the same academic period across every schedule-type
// variant instead of silently drifting per variant.
const NEW_SCHOOL_PERIOD_VALUE = '__new__';

const ScheduleForm = ({
  periods,
  onPeriodsChange,
  schoolConfig,
  schoolPeriods = [],
  onCreateSchoolPeriod,
  disabled = false,
}: ScheduleFormProps) => {
  const schoolPeriodById = new Map<number | null, ScheduleFormSchoolPeriod>(
    schoolPeriods.map((sp) => [sp.id, sp])
  );

  const updatePeriod = <K extends keyof ScheduleFormPeriod>(
    index: number,
    field: K,
    value: ScheduleFormPeriod[K]
  ) => {
    const updatedPeriods = [...periods];
    updatedPeriods[index] = { ...updatedPeriods[index], [field]: value };
    onPeriodsChange(updatedPeriods);
  };

  const addPeriod = (abDay: ScheduleFormAbDay = null) => {
    const newPeriod: ScheduleFormPeriod = {
      schoolPeriodId: null,
      start: '08:30',
      end: '09:30',
      abDay,
    };
    onPeriodsChange([...periods, newPeriod]);
  };

  const removePeriod = (index: number) => {
    // Guard destructive removal with a confirm, matching the variant-delete
    // pattern in ScheduleVariantModal (handleDelete). Dropping a configured
    // period row is easy to do by accident and can't be undone short of
    // re-entering it, so require an explicit confirmation first.
    if (
      !window.confirm('Remove this period? This cannot be undone.')
    ) {
      return;
    }

    const updatedPeriods = periods.filter((_, i) => i !== index);
    onPeriodsChange(updatedPeriods);
  };

  const duplicatePeriodFromAToB = (aPeriodIndex: number) => {
    const aPeriod = periods[aPeriodIndex];
    if (!aPeriod || aPeriod.abDay !== 'A') return;

    const bPeriod: ScheduleFormPeriod = {
      schoolPeriodId: aPeriod.schoolPeriodId,
      start: aPeriod.start,
      end: aPeriod.end,
      abDay: 'B',
    };
    onPeriodsChange([...periods, bPeriod]);
  };

  const autoFillBFromA = () => {
    const aPeriods = periods.filter((p) => p.abDay === 'A');
    const newBPeriods: ScheduleFormPeriod[] = aPeriods.map((period) => ({
      schoolPeriodId: period.schoolPeriodId,
      start: period.start,
      end: period.end,
      abDay: 'B',
    }));

    // Remove existing B periods and add new ones
    const nonBPeriods = periods.filter((p) => p.abDay !== 'B');
    onPeriodsChange([...nonBPeriods, ...newBPeriods]);
  };

  const handleSchoolPeriodChange = async (
    index: number,
    period: ScheduleFormPeriod,
    rawValue: string
  ) => {
    if (rawValue !== NEW_SCHOOL_PERIOD_VALUE) {
      updatePeriod(index, 'schoolPeriodId', rawValue ? Number(rawValue) : null);
      return;
    }

    const name = window.prompt('New period name (e.g., Period 7, Advisory):');
    if (!name || !name.trim()) {
      // Re-assert the controlled value — nothing else forces the <select>
      // back off the raw "+ New period..." option the browser applied
      // before this handler ran, since we're not otherwise updating state.
      updatePeriod(index, 'schoolPeriodId', period.schoolPeriodId);
      return;
    }

    const created = await onCreateSchoolPeriod(name.trim());
    updatePeriod(index, 'schoolPeriodId', created ? created.id : period.schoolPeriodId);
  };

  const renderPeriodForm = (
    period: ScheduleFormPeriod,
    index: number,
    showABControls = false
  ): React.ReactElement => {
    // Per-row unique ids so each label ties to its own control — the form
    // renders one of these blocks per period, so a shared id would break the
    // label/control association for every row after the first.
    const nameId = `period-name-${index}`;
    const startId = `period-start-${index}`;
    const endId = `period-end-${index}`;

    return (
    <div key={index} className={styles['period-row']}>
      <div className={styles['period-form-grid']}>
        <div className={styles['form-field']}>
          <label htmlFor={nameId}>Period Name</label>
          <select
            id={nameId}
            value={period.schoolPeriodId ?? ''}
            onChange={(e) =>
              handleSchoolPeriodChange(index, period, e.target.value)
            }
            disabled={disabled}
            className={styles['period-name-input']}
          >
            <option value="">-- Select a period --</option>
            {schoolPeriods.map((sp) => (
              <option key={sp.id} value={sp.id}>
                {sp.name}
              </option>
            ))}
            <option value={NEW_SCHOOL_PERIOD_VALUE}>+ New period...</option>
          </select>
        </div>

        <div className={styles['form-field']}>
          <label htmlFor={startId}>Start Time</label>
          <input
            id={startId}
            type="time"
            value={period.start || '08:30'}
            onChange={(e) => updatePeriod(index, 'start', e.target.value)}
            disabled={disabled}
            className={styles['time-input']}
          />
        </div>

        <div className={styles['form-field']}>
          <label htmlFor={endId}>End Time</label>
          <input
            id={endId}
            type="time"
            value={period.end || '09:30'}
            onChange={(e) => updatePeriod(index, 'end', e.target.value)}
            disabled={disabled}
            className={styles['time-input']}
          />
        </div>

        <div className={`${styles['form-field']} ${styles['actions-field']}`}>
          {showABControls && period.abDay === 'A' && (
            <button
              type="button"
              onClick={() => duplicatePeriodFromAToB(index)}
              disabled={disabled}
              className={styles['btn-copy']}
              title="Copy to B day"
            >
              Copy→B
            </button>
          )}
          <button
            type="button"
            onClick={() => removePeriod(index)}
            disabled={disabled}
            className={styles['btn-remove']}
            title="Remove period"
          >
            ×
          </button>
        </div>
      </div>
    </div>
    );
  };

  const renderRegularSchedule = () => {
    const regularPeriods = periods.filter((p) => !p.abDay);

    return (
      <div className={styles['schedule-section']}>
        <div className={styles['section-header']}>
          <h3>Daily Schedule</h3>
          <button
            type="button"
            onClick={() => addPeriod(null)}
            disabled={disabled}
            className={styles['btn-add-period']}
          >
            + Add Period
          </button>
        </div>

        {regularPeriods.length > 0 && (
          <div className={styles['period-header']}>
            <span className={styles['header-period-name']}>Period Name</span>
            <span>Start Time</span>
            <span>End Time</span>
            <span>Remove</span>
          </div>
        )}

        <div className={styles['periods-container']}>
          {regularPeriods.map((period) => {
            const actualIndex = periods.indexOf(period);
            return renderPeriodForm(period, actualIndex);
          })}
        </div>

        {regularPeriods.length === 0 && (
          <div className={styles['empty-state']}>
            <p>No periods configured yet. Click "Add Period" to get started.</p>
          </div>
        )}
      </div>
    );
  };

  const renderBlockSchedule = () => {
    const aPeriods = periods.filter((p) => p.abDay === 'A');
    const bPeriods = periods.filter((p) => p.abDay === 'B');
    const sharedPeriods = periods.filter(
      (p) => p.abDay === 'Both' || p.abDay === 'both'
    );

    return (
      <div className={styles['block-schedule-layout']}>
        <div className={styles['schedule-section']}>
          <div className={styles['section-header']}>
            <h3>A Day Periods</h3>
            <div className={styles['header-buttons']}>
              <button
                type="button"
                onClick={autoFillBFromA}
                disabled={disabled || aPeriods.length === 0}
                className={styles['btn-autofill']}
                title="Copy all A day periods to B day"
              >
                Auto-fill B Day
              </button>
              <button
                type="button"
                onClick={() => addPeriod('A')}
                disabled={disabled}
                className={styles['btn-add-period']}
              >
                + Add A Period
              </button>
            </div>
          </div>

          {aPeriods.length > 0 && (
            <div className={styles['period-header']}>
              <span className={styles['header-period-name']}>Period Name</span>
              <span>Start Time</span>
              <span>End Time</span>
              <span>Remove</span>
            </div>
          )}

          <div className={styles['periods-container']}>
            {aPeriods.map((period) => {
              const actualIndex = periods.indexOf(period);
              return renderPeriodForm(period, actualIndex, true);
            })}
          </div>

          {aPeriods.length === 0 && (
            <div className={styles['empty-state']}>
              <p>No A day periods configured yet.</p>
            </div>
          )}
        </div>

        <div className={styles['schedule-section']}>
          <div className={styles['section-header']}>
            <h3>B Day Periods</h3>
            <button
              type="button"
              onClick={() => addPeriod('B')}
              disabled={disabled}
              className={styles['btn-add-period']}
            >
              + Add B Period
            </button>
          </div>

          {bPeriods.length > 0 && (
            <div className={styles['period-header']}>
              <span className={styles['header-period-name']}>Period Name</span>
              <span>Start Time</span>
              <span>End Time</span>
              <span>Remove</span>
            </div>
          )}

          <div className={styles['periods-container']}>
            {bPeriods.map((period) => {
              const actualIndex = periods.indexOf(period);
              return renderPeriodForm(period, actualIndex);
            })}
          </div>

          {bPeriods.length === 0 && (
            <div className={styles['empty-state']}>
              <p>No B day periods configured yet.</p>
            </div>
          )}
        </div>

        <div className={styles['schedule-section']}>
          <div className={styles['section-header']}>
            <h3>Shared Periods</h3>
            <button
              type="button"
              onClick={() => addPeriod('Both')}
              disabled={disabled}
              className={styles['btn-add-period']}
            >
              + Add Shared Period
            </button>
          </div>

          {sharedPeriods.length > 0 && (
            <div className={styles['period-header']}>
              <span className={styles['header-period-name']}>Period Name</span>
              <span>Start Time</span>
              <span>End Time</span>
              <span>Remove</span>
            </div>
          )}

          <div className={styles['periods-container']}>
            {sharedPeriods.map((period) => {
              const actualIndex = periods.indexOf(period);
              return renderPeriodForm(period, actualIndex);
            })}
          </div>

          {sharedPeriods.length === 0 && (
            <div className={styles['empty-state']}>
              <p>
                Shared periods (like Advisory, Lunch) appear on both A and B
                days.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderMixedSchedule = () => {
    // Mixed schools can have both regular and block variants
    // For now, treat as block schedule - schools can create both types of variants
    return renderBlockSchedule();
  };

  if (!schoolConfig) {
    return (
      <div className={styles['schedule-form']}>
        <div className={styles['loading-state']}>
          <p>Loading school configuration...</p>
        </div>
      </div>
    );
  }

  const { schoolType } = schoolConfig;

  return (
    <div className={styles['schedule-form']}>
      <div className={styles['form-info']}>
        <p>
          <strong>School Type:</strong> {schoolType} |
          <strong> Total Periods:</strong> {schoolConfig.totalPeriods}
        </p>
        <small>
          Configure the schedule for this variant. Times should be in 24-hour
          format.
        </small>
      </div>

      {schoolType === 'Regular' && renderRegularSchedule()}
      {schoolType === 'Block' && renderBlockSchedule()}
      {schoolType === 'Mixed' && renderMixedSchedule()}

      <div className={styles['period-summary']}>
        <h4>Period Summary</h4>
        <div className={styles['summary-stats']}>
          <div className={styles['stat']}>
            <span className={styles['stat-label']}>Total Periods:</span>
            <span className={styles['stat-value']}>{periods.length}</span>
          </div>

          {schoolType !== 'Regular' && (
            <>
              <div className={styles['stat']}>
                <span className={styles['stat-label']}>A Day:</span>
                <span className={styles['stat-value']}>
                  {periods.filter((p) => p.abDay === 'A').length}
                </span>
              </div>
              <div className={styles['stat']}>
                <span className={styles['stat-label']}>B Day:</span>
                <span className={styles['stat-value']}>
                  {periods.filter((p) => p.abDay === 'B').length}
                </span>
              </div>
              <div className={styles['stat']}>
                <span className={styles['stat-label']}>Shared:</span>
                <span className={styles['stat-value']}>
                  {
                    periods.filter(
                      (p) => p.abDay === 'Both' || p.abDay === 'both'
                    ).length
                  }
                </span>
              </div>
            </>
          )}

          <div className={styles['stat']}>
            <span className={styles['stat-label']}>Numbered Periods:</span>
            <span className={styles['stat-value']}>
              {
                periods.filter((p) => {
                  const name = schoolPeriodById.get(p.schoolPeriodId)?.name;
                  return name && name.match(/^Period \d+$/);
                }).length
              }
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScheduleForm;
